import { fileToBase64, formattedDataSelect } from "@app/common/utils/utils";
import routes from "@app/config/routes";
import { useGetDocumentTypes } from "@app/services/common/hooks";
import { useRequestDeliveryFull } from "@app/services/deliveryAuth/hooks";
import { RequestFullDeliveryPayload } from "@app/services/deliveryAuth/types";
import { OrderInfo } from "@app/services/drugDelivery/types";
import { useDeliveryData } from "@app/store/useDeliverySearchData";
import { Box, Button, Drawer, FileButton, Select, Text, TextInput, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconCamera, IconUpload } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DeliveryRegisterPicture from "./DeliveryRegisterPicture";
import "../styles.scss";

const CompleteDeliveryRequestForm = ({
  isOpen,
  onClose,
  dataPatient
}: {
  isOpen: boolean;
  dataPatient?: OrderInfo;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const clearDataDispense = useDeliveryData((store) => store.clearDataDispense);

  const [evidencePhoto, setEvidencePhoto] = useState<string>("");
  const [supportFile, setSupportFile] = useState<string>("");
  const [supportFileName, setSupportFileName] = useState<string>("");
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      rcdReason: "",
      rcdPatientName: "",
      rcdPatientDocument: "",
      rcdPatientDocType: "",
      rcdPatientEmail: "",
      rcdPatientPhone: ""
    },

    validate: {
      rcdReason: (value) => (value !== "" ? null : "Campo obligatorio"),
      rcdPatientName: (value) =>
        value.trim().length >= 2 ? null : "Este campo requiere al menos 2 caracteres",
      rcdPatientDocument: (value) =>
        /^[a-zA-Z0-9]{4,}$/.test(value) ? null : "Este campo requiere al menos 4 caracteres",
      rcdPatientDocType: (value) => (value !== "" ? null : "Campo obligatorio"),
      rcdPatientEmail: (value) => (/^\S+@\S+$/.test(value) ? null : "Correo invalido"),
      rcdPatientPhone: (value) =>
        /^[0-9]+$/.test(value) ? null : "Debe contener solo caracteres numéricos"
    }
  });

  const {
    data: documentType = {
      results: [],
      rowTotal: 0
    }
  } = useGetDocumentTypes();

  const closeDrawer = () => {
    form.setValues({
      rcdReason: "",
      rcdPatientName: "",
      rcdPatientDocument: "",
      rcdPatientDocType: "",
      rcdPatientEmail: "",
      rcdPatientPhone: ""
    });
    setEvidencePhoto("");
    setSupportFile("");
    setSupportFileName("");
    onClose();
  };

  const handlePhotoChange = (photo: string) => {
    setEvidencePhoto(photo);
  };

  const handleSupportFileChange = async (file: File | null) => {
    if (file) {
      const base64 = await fileToBase64(file);
      setSupportFile(base64);
      setSupportFileName(file.name);
    }
  };

  const createDeliveryRequest = useRequestDeliveryFull(async () => {
    const doAfter = () => {
      closeDrawer();
      navigate(routes.drugDelivery);
      clearDataDispense();
    };

    await doAfter();
  });

  const onSubmit = async (value: RequestFullDeliveryPayload) => {
    const payload: RequestFullDeliveryPayload = {
      ...value,
      supports: {}
    };

    if (payload.supports && evidencePhoto) {
      payload.supports.photography = evidencePhoto;
    }

    if (payload.supports && supportFile) {
      payload.supports.attachment = supportFile;
    }

    await createDeliveryRequest.mutateAsync(payload);
  };

  useEffect(() => {
    if (dataPatient?.patient && dataPatient?.orderId) {
      form.setValues({
        rcdPatientName: dataPatient.patient?.name,
        rcdPatientDocument: dataPatient.patient?.document,
        rcdPatientDocType: formattedDataSelect(documentType.results, "description", "id").find(
          (x) => x.label === dataPatient.patient?.documentType
        )?.value,
        rcdPatientEmail: dataPatient.patient?.email,
        rcdPatientPhone: dataPatient.patient?.phone
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPatient?.patient, isOpen, documentType.results, dataPatient?.orderId]);

  return (
    <>
      <DeliveryRegisterPicture
        data={evidencePhoto}
        isOpen={isPhotoModalOpen}
        setData={handlePhotoChange}
        onClose={() => setIsPhotoModalOpen(false)}
      />

      <Drawer
        opened={isOpen}
        onClose={closeDrawer}
        title={"Solicitud de entrega completa"}
        size={"xs"}
        data-testid="completedeliveryrequestform-drawer"
      >
        <form
          onSubmit={form.onSubmit((value) =>
            onSubmit({
              ...value,
              cluId: Number(dataPatient?.patient.id),
              morId: Number(dataPatient?.orderId)
            })
          )}
          className="full"
          data-testid="completedeliveryrequestform-form"
        >
          <Box
            className="flex flex-column justify-between"
            h={"100%"}
            data-testid="completedeliveryrequestform-mainbox"
          >
            <Box w={"100%"} data-testid="completedeliveryrequestform-fieldsbox">
              <TextInput
                label={"Nombre"}
                type="text"
                data-testid="completedeliveryrequestform-patientname-input"
                placeholder="Escribir..."
                key={form.key("rcdPatientName")}
                {...form.getInputProps("rcdPatientName")}
              />

              <Select
                label="Tipo de identificación"
                data={formattedDataSelect(documentType.results, "description", "id")}
                data-testid="completedeliveryrequestform-patientdoctype-select"
                placeholder="Seleccionar..."
                key={form.key("rcdPatientDocType")}
                {...form.getInputProps("rcdPatientDocType")}
              />

              <TextInput
                type="text"
                label="No. Identificación"
                data-testid="completedeliveryrequestform-patientdocument-input"
                placeholder="Escribir..."
                key={form.key("rcdPatientDocument")}
                {...form.getInputProps("rcdPatientDocument")}
              />

              <TextInput
                data-testid="completedeliveryrequestform-patientinsurance-input"
                label="Aseguradora"
                readOnly
                placeholder="Escribir..."
                value={dataPatient?.contract}
              />
              <TextInput
                data-testid="completedeliveryrequestform-patientphone-input"
                label="Celular"
                type="text"
                placeholder="Escribir..."
                key={form.key("rcdPatientPhone")}
                {...form.getInputProps("rcdPatientPhone")}
              />

              <TextInput
                type="email"
                label="Correo electrónico"
                data-testid="completedeliveryrequestform-patientemail-input"
                key={form.key("rcdPatientEmail")}
                {...form.getInputProps("rcdPatientEmail")}
                placeholder="Escribir..."
              />

              <Textarea
                label="Motivo de solicitud"
                data-testid="completedeliveryrequestform-reason-input"
                placeholder="Escribir..."
                key={form.key("rcdReason")}
                {...form.getInputProps("rcdReason")}
              />

              <Box mt="md" data-testid="completedeliveryrequestform-evidencephoto-container">
                <Text fz="xs" c="secondary-color">
                  Tomar foto
                </Text>
                <div className="cardCamera" data-testid="completedeliveryrequestform-photoarea">
                  {evidencePhoto ? (
                    <img
                      src={
                        evidencePhoto.startsWith("http")
                          ? evidencePhoto
                          : `data:image/jpeg;base64,${evidencePhoto}`
                      }
                      alt="evidence"
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "2%"
                      }}
                    />
                  ) : (
                    <Text fz="md" ml="xs" fw={"inherit"}>
                      Tomar foto
                    </Text>
                  )}
                  <div className="cameraButton">
                    <IconCamera
                      style={{ width: 20, height: 20 }}
                      className="primary-color pointer"
                      data-testid="completedeliveryrequestform-takephoto-button"
                      onClick={() => setIsPhotoModalOpen(true)}
                    />
                  </div>
                </div>
              </Box>

              <Box my="md" data-testid="completedeliveryrequestform-supportfiles-container">
                <FileButton
                  onChange={handleSupportFileChange}
                  accept="application/pdf,image/jpeg,image/png"
                >
                  {(props) => (
                    <Button
                      {...props}
                      variant="light"
                      fullWidth
                      data-testid="completedeliveryrequestform-uploadsupport-button"
                      leftSection={<IconUpload />}
                    >
                      Adjuntar soporte
                    </Button>
                  )}
                </FileButton>
                {supportFileName && (
                  <Text fz="xs" mt="xs" c="secondary-color" fw={"bold"}>
                    Archivo adjunto: {supportFileName}
                  </Text>
                )}
              </Box>
            </Box>
            <Box
              className="flex justify-end gap-3"
              pb="lg"
              data-testid="completedeliveryrequestform-buttonsbox-box"
            >
              <Button
                variant="outline"
                onClick={closeDrawer}
                data-testid="completedeliveryrequestform-cancel-button"
              >
                Cancelar
              </Button>
              <Button
                variant="filled"
                type="submit"
                data-testid="completedeliveryrequestform-submit-button"
              >
                {" "}
                Enviar
              </Button>
            </Box>
          </Box>
        </form>
      </Drawer>
    </>
  );
};

export default CompleteDeliveryRequestForm;
